import React, { useRef, useState } from 'react';
import { Button } from '@rocco/components/button/button';
import type {
    TileViewModel,
    TileableModel,
} from '@rocco/ui/tile-shared/view-models/tile';
import { CategoryPageTileWithAdminActions } from '../category-page/CategoryPageTileWithAdminActions';
import { createSectionScrollOnCollapse } from '../../utils/use-section-scroll-on-collapse';

interface Props {
    title: React.ReactNode;
    titleClassName?: string;
    tiles: TileViewModel<TileableModel>[];
    /** Show a border line under the section title */
    showTitleBorder?: boolean;
}

const DEFAULT_TITLE_CLASS = 'pt-5 text-5 font-medium tracking-[0.025rem]';

/**
 * Grid layout for featured professionals on details pages.
 * Used on both product and project details pages with configurable title styling.
 * When titleClassName is provided, it fully replaces the default title classes.
 */
export const DetailsPageProfessionalTileGrid = ({
    title,
    titleClassName,
    tiles,
    showTitleBorder,
}: Props) => {
    const [expanded, setExpanded] = useState(false);
    const sectionRef = useRef<HTMLDivElement>(null);
    const tilesToShow = expanded ? tiles.length : Math.min(tiles.length, 8);

    const handleToggle = createSectionScrollOnCollapse(
        expanded,
        setExpanded,
        sectionRef,
    );

    return (
        <div ref={sectionRef}>
            <div className="pb-8">
                <h2 className={titleClassName ?? DEFAULT_TITLE_CLASS}>
                    {title ? title : 'Professionals used'}
                </h2>
                {showTitleBorder && (
                    <div className="relative w-full border-t border-[#141414]/20" />
                )}
            </div>
            <div
                className="grid gap-x-[1.1rem] gap-y-18"
                style={{
                    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                }}
            >
                {tiles.slice(0, tilesToShow).map((tile, idx) => (
                    <div key={idx} className="w-full">
                        <div className="h-full w-full overflow-hidden">
                            <CategoryPageTileWithAdminActions tile={tile} />
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-30 flex justify-center">
                {tiles.length >= 9 ? (
                    <Button
                        onClick={handleToggle}
                        color="primary"
                        size="larger"
                        className="min-w-53.5"
                    >
                        {expanded ? 'View Less' : 'View More'}
                    </Button>
                ) : (
                    <Button
                        className="invisible min-w-53.5"
                        color="primary"
                        size="larger"
                        aria-hidden="true"
                        tabIndex={-1}
                    >
                        &nbsp;
                    </Button>
                )}
            </div>
        </div>
    );
};
